import React, { useState, useRef, useEffect } from "react";
import { getProfilePicture } from "../utils/avatar";

/**
 * A textarea with @mention autocomplete functionality.
 * Shows a dropdown of users when "@" is typed.
 */
const MentionInput = ({
  value,
  onChange,
  placeholder,
  rows = 2,
  className = "",
  mentionableUsers = [], // Array of { username, _id } objects
  disabled = false,
}) => {
  const [showMentionPopup, setShowMentionPopup] = useState(false);
  const [mentionFilter, setMentionFilter] = useState("");
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const textareaRef = useRef(null);
  const popupRef = useRef(null);

  // Get unique usernames from mentionableUsers
  const uniqueUsers = React.useMemo(() => {
    const seen = new Set();
    return mentionableUsers.filter((user) => {
      if (!user?.username || seen.has(user.username.toLowerCase())) {
        return false;
      }
      seen.add(user.username.toLowerCase());
      return true;
    });
  }, [mentionableUsers]);

  // Filter users based on what's typed after @
  const filteredUsers = React.useMemo(() => {
    if (!mentionFilter) return uniqueUsers;
    const filter = mentionFilter.toLowerCase();
    return uniqueUsers.filter((user) =>
      user.username.toLowerCase().startsWith(filter)
    );
  }, [uniqueUsers, mentionFilter]);

  // Reset selected index when filtered list changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredUsers.length]);

  // Handle click outside to close popup
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        !textareaRef.current.contains(e.target)
      ) {
        setShowMentionPopup(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;

    onChange(newValue);

    // Check if we should show mention popup
    // Look backwards from cursor to find @ symbol
    let atIndex = -1;
    for (let i = cursorPos - 1; i >= 0; i--) {
      const char = newValue[i];
      if (char === "@") {
        // Check if @ is at start or preceded by whitespace
        if (i === 0 || /\s/.test(newValue[i - 1])) {
          atIndex = i;
          break;
        }
      }
      // Stop if we hit whitespace (no @ in this "word")
      if (/\s/.test(char)) break;
    }

    if (atIndex !== -1) {
      const textAfterAt = newValue.substring(atIndex + 1, cursorPos);
      // Only show popup if text after @ is valid (alphanumeric, underscore, dot, or empty)
      if (/^[a-zA-Z0-9_.]*$/.test(textAfterAt)) {
        setMentionStartIndex(atIndex);
        setMentionFilter(textAfterAt);
        setShowMentionPopup(true);

        // Calculate popup position — open above if not enough space below
        if (textareaRef.current) {
          const rect = textareaRef.current.getBoundingClientRect();
          const spaceBelow = window.innerHeight - rect.bottom;
          const popupHeight = 200; // approximate max-h-48 = 192px
          if (spaceBelow < popupHeight) {
            // Open above the textarea
            setPopupPosition({
              bottom: rect.height + 4,
              top: "auto",
              left: 0,
            });
          } else {
            setPopupPosition({
              top: rect.height + 4,
              bottom: "auto",
              left: 0,
            });
          }
        }
      } else {
        setShowMentionPopup(false);
      }
    } else {
      setShowMentionPopup(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!showMentionPopup || filteredUsers.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredUsers.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredUsers.length - 1
        );
        break;
      case "Enter":
      case "Tab":
        if (filteredUsers[selectedIndex]) {
          e.preventDefault();
          selectUser(filteredUsers[selectedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setShowMentionPopup(false);
        break;
      default:
        break;
    }
  };

  const selectUser = (user) => {
    if (!user || mentionStartIndex === -1) return;

    const textarea = textareaRef.current;
    const cursorPos = textarea.selectionStart;

    // Replace @filter with @username
    const beforeMention = value.substring(0, mentionStartIndex);
    const afterCursor = value.substring(cursorPos);
    const newValue = `${beforeMention}@${user.username} ${afterCursor}`;

    onChange(newValue);
    setShowMentionPopup(false);
    setMentionFilter("");
    setMentionStartIndex(-1);

    // Set cursor position after the inserted mention
    const newCursorPos = mentionStartIndex + user.username.length + 2; // +2 for @ and space
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };


  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        className={`spiders-input w-full resize-y max-h-[200px] ${className}`}
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        rows={rows}
        disabled={disabled}
      />

      {/* Mention Popup */}
      {showMentionPopup && filteredUsers.length > 0 && (
        <div
          ref={popupRef}
          className="absolute z-50 bg-spiders-bg-elevated border border-spiders-border rounded-lg shadow-xl max-h-48 overflow-y-auto w-64"
          style={{ top: popupPosition.top, left: popupPosition.left }}
        >
          <div className="py-1">
            <div className="px-3 py-1.5 text-xs text-spiders-text-muted border-b border-spiders-border">
              People in this thread
            </div>
            {filteredUsers.map((user, index) => (
              <button
                key={user._id || user.username}
                className={`w-full flex items-center space-x-2 px-3 py-2 text-left transition-colors ${
                  index === selectedIndex
                    ? "bg-spiders-primary-500/20 text-spiders-text-primary"
                    : "text-spiders-text-secondary hover:bg-spiders-bg-surface"
                }`}
                onClick={() => selectUser(user)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <img
                  src={getProfilePicture(user.username, user.avatarSeed)}
                  alt={user.username}
                  className="w-7 h-7 rounded-full border border-spiders-border"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">
                    {user.username}
                  </div>
                  <div className="text-xs text-spiders-text-muted truncate">
                    @{user.username}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* No matches message */}
      {showMentionPopup && filteredUsers.length === 0 && mentionFilter && (
        <div
          ref={popupRef}
          className="absolute z-50 bg-spiders-bg-elevated border border-spiders-border rounded-lg shadow-xl w-64"
          style={{ top: popupPosition.top, left: popupPosition.left }}
        >
          <div className="px-3 py-3 text-sm text-spiders-text-muted">
            No users found matching "@{mentionFilter}"
          </div>
        </div>
      )}
    </div>
  );
};

export default MentionInput;
